import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Stack,
  Grid,
  Typography,
  Chip,
  Divider,
  CardContent,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Delete as DeleteIcon, Person as PersonIcon, Group as GroupIcon, Info as InfoIcon, Settings as SettingsIcon } from '@mui/icons-material';

import { Container, Input, ErrorMessage, InfoLabel, InfoValue, SectionHeader, StyledCard, LoadingState, ErrorState, EmptyState, CustomSwitch, DataGrid } from '@/components/common';
import { useUserDetail, useUpdateUser, useDeleteUser, useUpdateUserStatus } from '@/hooks/users.hook';
import { useManagerGroups } from '@/hooks/manager.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { users } from '@/constants';
import { formatDate } from '@/util/dateUtils';
import { isActiveStatus, nextToggleStatus, USER_STATUS, userStatusChangeMessage, userStatusLabel } from '@/utils/userStatus';
import GroupCourseCell from '../components/GroupCourseCell';
import AddGroupModal from './components/AddGroupModal';

/**
 * 매니저 상세 페이지 컴포넌트
 * 
 * 매니저의 상세 정보를 조회하고 수정할 수 있는 페이지입니다.
 */

const ManagerDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  // 모달 상태 관리
  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);

  // 매니저 상세 정보 조회
  const { data: detailData, isLoading, error } = useUserDetail(userId);
  const { data: groupList, isLoading: isGroupListLoading, error: groupListError, refetch: refetchGroupList } = useManagerGroups(userId);

  // API 응답 구조
  const userData = detailData;

  // 수정, 삭제, 상태 변경 훅
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const updateStatusMutation = useUpdateUserStatus();

  // 폼 관리
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      userId: '',
      name: '',
      email: '',
    },
  });

  // 데이터 로드 시 폼에 값 설정
  useEffect(() => {
    if (userData) {
      reset({
        userId: userData.userId || '',
        name: userData.name || '',
        email: userData.email || '',
      });
    }
  }, [userData, reset]);

  /**
   * 매니저 정보 수정 핸들러
   */
  const onSubmit = useCallback(
    (data) => {
      updateUserMutation.mutate(
        {
          userId: data.userId,
          name: data.name,
          email: data.email,
        },
        {
          onSuccess: () => {
            successToast('매니저 정보가 수정되었습니다.');
          },
          onError: (error) => {
            errorToast(`매니저 정보 수정에 실패했습니다. - ${error.response?.data?.msg}`);
          },
        }
      );
    },
    [updateUserMutation]
  );

  /**
   * 매니저 삭제 핸들러
   */
  const handleDelete = useCallback(() => {
    confirmToast('정말로 이 매니저를 삭제하시겠습니까?', () => {
      deleteUserMutation.mutate(userId, {
        onSuccess: () => {
          successToast('매니저가 삭제되었습니다.');
          navigate('/managers');
        },
        onError: (error) => {
          errorToast(`매니저 삭제에 실패했습니다. - ${error.response?.data?.msg}`);
        },
      });
    });
  }, [userId, deleteUserMutation, navigate]);

  /**
   * 상태 변경 핸들러
   */
  const handleStatusChange = useCallback(() => {
    const newStatus = nextToggleStatus(userData?.status);

    updateStatusMutation.mutate(
      { userId, status: newStatus },
      {
        onSuccess: () => {
          successToast('매니저 상태가 변경되었습니다.');
        },
        onError: (error) => {
          errorToast(`상태 변경에 실패했습니다. - ${error.response?.data?.msg}`);
        },
      }
    );
  }, [userId, userData?.status, updateStatusMutation]);

  /**
   * 뒤로가기 핸들러
   */
  const handleGoBack = useCallback(() => {
    navigate('/managers');
  }, [navigate]);


  // 그룹 목록 컬럼 정의
  const groupColumns = useMemo(() => [
    {
      field: 'groupId',
      headerName: '그룹ID',
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'groupName',
      headerName: '고객사명',
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'groupCourse',
      headerName: '그룹명',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => <GroupCourseCell row={params.row} />,
    }
  ], []);

  // 로딩 중
  if (isLoading) {
    return <LoadingState title='매니저 상세' />;
  }

  // 에러 발생
  if (error) {
    return <ErrorState title='매니저 상세' message='매니저 정보를 불러오는 중 오류가 발생했습니다.' onGoBack={handleGoBack} />;
  }

  // 데이터 없음
  if (!userData) {
    return <EmptyState title='매니저 상세' message='매니저 정보를 찾을 수 없습니다.' onGoBack={handleGoBack} />;
  }

  return (
    <Container title='매니저 상세'>
      <Stack spacing={3}>
        {/* 상단 버튼 영역 */}
        <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
          <Button 
            variant='outlined' 
            onClick={handleGoBack} 
            startIcon={<ArrowBackIcon />}
            sx={{ minWidth: 120 }}
          >
            목록으로
          </Button>
          <Stack direction='row' spacing={1}>
            <Button
              variant='outlined'
              color='error'
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
              disabled={deleteUserMutation.isPending}
              sx={{ minWidth: 100 }}
            >
              삭제
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit(onSubmit)}
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateUserMutation.isPending}
              sx={{ minWidth: 100 }}
            >
              저장
            </Button>
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {/* 왼쪽: 매니저 기본 정보 */}
          <Grid item xs={12} md={8}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <SectionHeader>
                  <PersonIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                  <Typography variant='h5' fontWeight={600}>
                    매니저 정보
                  </Typography>
                </SectionHeader>

                <Grid container spacing={3}>
                  {/* 사용자 ID */}
                  <Grid item xs={12} sm={6}>
                    <InfoLabel>사용자 ID</InfoLabel>
                    <Input
                      control={control}
                      name='userId'
                      textFieldProps={{
                        fullWidth: true,
                        disabled: true,
                        variant: 'outlined',
                        sx: { '& .MuiInputBase-input': { fontWeight: 500 } }
                      }}
                    />
                  </Grid>

                  {/* 이름 */}
                  <Grid item xs={12} sm={6}>
                    <InfoLabel>이름 *</InfoLabel>
                    <Input
                      control={control}
                      name='name'
                      textFieldProps={{
                        fullWidth: true,
                        required: true,
                        variant: 'outlined',
                      }}
                      rules={{
                        required: '이름을 입력해주세요.',
                      }}
                    />
                    <ErrorMessage errors={errors} name='name' />
                  </Grid>

                  {/* 이메일 */}
                  <Grid item xs={12} sm={6}>
                    <InfoLabel>이메일 *</InfoLabel>
                    <Input
                      control={control}
                      name='email'
                      textFieldProps={{
                        fullWidth: true,
                        type: 'email',
                        required: true,
                        variant: 'outlined',
                      }}
                      rules={{
                        required: '이메일을 입력해주세요.',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: '올바른 이메일 형식이 아닙니다.',
                        },
                      }}
                    />
                    <ErrorMessage errors={errors} name='email' />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* 오른쪽: 상태 및 권한 정보 */}
          <Grid item xs={12} md={4}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <SectionHeader>
                  <InfoIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                  <Typography variant='h6' fontWeight={600}>
                    상태 정보
                  </Typography>
                </SectionHeader>

                <Stack spacing={3}>
                  {/* 권한 */}
                  <Box>
                    <InfoLabel>권한</InfoLabel>
                    <Box mt={1}>
                      <Chip
                        label={users.USER_TYPE_LABEL_MAP[userData?.userRole] || userData?.userRole || '-'}
                        color={users.USER_TYPE_COLOR_MAP[userData?.userRole] || 'default'}
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          height: 32,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* 상태 */}
                  <Box>
                    <InfoLabel>상태</InfoLabel>
                    <Box display='flex' alignItems='center' gap={1.5} mt={1}>
                      <CustomSwitch
                        value={isActiveStatus(userData?.status)}
                        onChangeToggle={handleStatusChange}
                        message={userStatusChangeMessage(userData?.status)}
                      />
                      <InfoValue>
                        {userStatusLabel(userData?.status)}
                      </InfoValue>
                    </Box>
                    {userData?.status === USER_STATUS.SECESSION && userData?.secessionOpinion && (
                      <Box mt={1.5}>
                        <InfoLabel>계정 잠금 사유</InfoLabel>
                        <InfoValue mt={1} sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                          {userData.secessionOpinion}
                        </InfoValue>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* 가입일 */}
                  <Box>
                    <InfoLabel>가입일</InfoLabel>
                    <InfoValue mt={1}>
                      {formatDate(userData?.createdAt) || '-'}
                    </InfoValue>
                  </Box>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
          {/* 그룹 정보 */}
          <Grid item xs={12}>
            {
              userData?.userRole !== 'ADMIN' && (
                <StyledCard>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <SectionHeader sx={{ margin: 0, padding: 0, border: 'none' }}>
                        <GroupIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                        <Typography variant='h5' fontWeight={600}>
                          그룹 정보 ({groupList?.length || 0}개)
                        </Typography>
                      </SectionHeader>
                      <Button
                        variant="contained"
                        startIcon={<SettingsIcon />}
                        onClick={() => setAddGroupModalOpen(true)}
                      >
                        관리 그룹 설정
                      </Button>
                    </Stack>

                    {groupList && groupList.length > 0 ? (
                      <DataGrid
                        rows={groupList ?? []}
                        columns={groupColumns}
                        loading={isGroupListLoading}
                        getRowId={(row) => row.groupId}
                        pagination={false}
                        height="auto"
                        noRowsMessage="등록된 그룹이 없습니다."
                        sx={{
                          '& .MuiDataGrid-row': {
                            cursor: 'default',
                          },
                          '& .MuiDataGrid-row:hover': {
                            cursor: 'default',
                          },
                        }}
                      />
                    ) : (
                      <Box sx={{ py: 4, textAlign: 'center', color: '#888' }}>
                        등록된 그룹이 없습니다.
                      </Box>
                    )}
                  </CardContent>
                </StyledCard>
              )
            }
          </Grid>
        </Grid>
      </Stack>

      {/* 그룹 추가 모달 */}
      <AddGroupModal
        open={addGroupModalOpen}
        onClose={() => setAddGroupModalOpen(false)}
        userId={userId}
        currentGroupIds={groupList?.map(group => group.groupId) || []}
        onSuccess={() => {
          refetchGroupList();
        }}
      />
    </Container>
  );
};

export default ManagerDetailPage;
